import { SeoSlugEnum } from '@rover/constants/src/search/seo.constants';
import { get } from '@rover/react-lib/src/apis/citiesAndNeighborhoods.api';
import fetchCitiesAndNeighborhoodsResponseFactory from '@rover/react-lib/src/factories/fetchCitiesAndNeighborhoodsResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import type { FetchCitiesAndNeighborhoodsResponse } from '@rover/types';

import generateActions from './generateActions';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchCitiesAndNeighborhoodsResponse: FetchCitiesAndNeighborhoodsResponse;
};

type Actions = {
  fetchCitiesAndNeighborhoodsPending: ActionCreator<void>;
  fetchCitiesAndNeighborhoodsFailure: ActionCreator<string>;
  fetchCitiesAndNeighborhoodsSuccess: ActionCreator<FetchCitiesAndNeighborhoodsResponse>;
};

type Effects = {
  fetchCitiesAndNeighborhoods: EffectCreator<{
    countryUrlPrefix: string;
    serviceType: SeoSlugEnum | null;
  }>;
};

type Selectors = {
  getCitiesAndNeighborhoods: SelectorCreator<FetchCitiesAndNeighborhoodsResponse>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'citiesAndNeighborhoods',
  initialState: {
    loading: false,
    errorMessage: '',
    fetchCitiesAndNeighborhoodsResponse: fetchCitiesAndNeighborhoodsResponseFactory(),
  },
  actions: {
    ...generateActions('fetchCitiesAndNeighborhoods'),
  },
  effects: {
    fetchCitiesAndNeighborhoods:
      ({ countryUrlPrefix, serviceType }) =>
      async (
        dispatch,
        getState,
        {
          fetchCitiesAndNeighborhoodsPending,
          fetchCitiesAndNeighborhoodsSuccess,
          fetchCitiesAndNeighborhoodsFailure,
        }
      ) => {
        dispatch(fetchCitiesAndNeighborhoodsPending());

        try {
          const fetchCitiesAndNeighborhoodsResponse = await get({
            countryUrlPrefix,
            serviceType,
          });
          dispatch(fetchCitiesAndNeighborhoodsSuccess(fetchCitiesAndNeighborhoodsResponse));
        } catch (e) {
          dispatch(fetchCitiesAndNeighborhoodsFailure((e as Error).message));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getCitiesAndNeighborhoods: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchCitiesAndNeighborhoodsResponse),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
